import type { Order } from './order';
import type {
  MakeOrderItem,
  MakeOrderPaymentInfo,
  MakeOrderDeliveryInfo,
  MakeOrderPaymentMethod,
} from './make-order';

// A troca da forma de pagamento de um pedido cuja cobrança falhou/foi recusada NÃO
// possui rota dedicada: a antiga PATCH `.../orders/{orderId}/payment-method` não
// existe no backend e respondia 404 (issue 288). Em vez disso, reenviamos a própria
// rota de criação de pedido (place-order) com a nova forma de pagamento — por isso o
// endpoint utilizado é o de make-order (ver `ENDPOINT` em `./make-order`).

// ID fixo do método de pagamento com cartão de crédito (igual ao usado no place-order).
export const CARD_PAYMENT_METHOD_ID = 900;

// Modo de pagamento do pedido: "online" (pago pelo app — PIX/cartão) ou "offline"
// (pago na entrega). O backend usa este campo para reprocessar a cobrança da forma
// correta na segunda tentativa: sem ele, um método "na entrega" seria tratado como
// cobrança online e a recriação do pedido falha (issue 288).
export type PaymentMode = 'online' | 'offline';

// Dados da nova forma de pagamento escolhida na segunda tentativa (falha/recusa).
export type ChangePaymentMethodBody = {
  payment_method: MakeOrderPaymentMethod; // novo método de pagamento escolhido
  cash_change_amount?: number; // valor da nota para troco (quando pagamento em dinheiro)
  payment_mode?: PaymentMode; // online (pague pelo app) ou offline (pague na entrega)
};

// Entrada da usecase: o pedido original (itens, loja e entrega são reaproveitados para
// recriá-lo na rota de place-order) + os dados do novo pagamento.
export type ChangePaymentMethodRequest = {
  order: Order;
} & ChangePaymentMethodBody;

// Corpo efetivamente enviado à rota de criação de pedido (place-order) ao reprocessar:
// reaproveita a tipagem do make-order e apenas troca a forma de pagamento. O
// `payment_mode` acompanha o corpo para o backend reprocessar a cobrança de forma
// consistente (online = pague pelo app, offline = na entrega).
export type ReplaceOrderBody = {
  items: MakeOrderItem[];
  payment_info: MakeOrderPaymentInfo;
  cash_change_amount: number;
  delivery_info: MakeOrderDeliveryInfo;
  payment_mode?: PaymentMode;
  store_slug: string;
};

// O backend retorna o pedido atualizado (status, external_payment, payment_info/qr_code, etc.),
// permitindo que a UI atualize o cache e retome o polling do pagamento.
export type ChangePaymentMethodResponse = Order;
